import axios from 'axios'
import { directoryApiClient } from './api/auth'
import { getAccessToken, saveTokens } from './authentication'
import { logEvent } from './datadog'

export class SsoForbiddenError extends Error {
  constructor() {
    super('Passkeys are not available for SSO accounts')
    this.name = 'SsoForbiddenError'
  }
}

export type WebAuthnCredential = {
  id: string
  external_id: string
  device_nickname: string | null
  last_used_at: string | null
  created_at: string
  icon_dark: string | null
  icon_light: string | null
}

const BASE64_PADDING = /=+$/

const toBase64Url = (base64: string): string =>
  base64.replace(/\+/g, '-').replace(/\//g, '_').replace(BASE64_PADDING, '')

type PublicKeyCredentialWithSignal = typeof PublicKeyCredential & {
  signalUnknownCredential?(options: { rpId: string; credentialId: string }): Promise<void>
}

const signalUnknownCredential = async (externalId: string): Promise<void> => {
  const publicKeyCredential = (typeof PublicKeyCredential !== 'undefined' ? PublicKeyCredential : undefined) as
    | PublicKeyCredentialWithSignal
    | undefined
  if (!publicKeyCredential?.signalUnknownCredential) {
    return
  }
  try {
    await publicKeyCredential.signalUnknownCredential({
      rpId: window.location.hostname,
      credentialId: toBase64Url(externalId),
    })
  } catch (error) {
    logEvent(
      'webauthn_signal_unknown_credential_error',
      undefined,
      { error: String(error) },
      error instanceof Error ? error : new Error(String(error)),
      'warn',
    )
  }
}

export type ServerRegistrationOptions = Omit<PublicKeyCredentialCreationOptions, 'user' | 'excludeCredentials'> & {
  user: Omit<PublicKeyCredentialUserEntity, 'id'> & { id: string }
  excludeCredentials?: Array<Omit<PublicKeyCredentialDescriptor, 'id'> & { id: string }>
}

type RegistrationAttestation = {
  id: string
  rawId: string
  type: string
  response: {
    clientDataJSON: string
    attestationObject: string
  }
}

type PasskeyBridgeRegistrationOptions = Omit<ServerRegistrationOptions, 'challenge'> & { challenge: string }

type PasskeyBridge = {
  // iOS resolves a Promise via postMessage; Android's addJavascriptInterface only exposes
  // synchronous methods returning a plain value — `await` handles both transparently.
  isAvailable?: () => boolean | Promise<boolean>
  // iOS takes/returns a JS object; Android's bridge can only marshal primitives across the
  // JS interface, so it takes/returns a JSON string instead.
  register?: (
    options: PasskeyBridgeRegistrationOptions | string,
  ) => RegistrationAttestation | string | Promise<RegistrationAttestation | string>
}

const getPasskeyBridge = (): PasskeyBridge | undefined =>
  (window as unknown as { PasskeyBridge?: PasskeyBridge }).PasskeyBridge

/**
 * Whether the native app has injected a passkey bridge (iOS WKWebView with
 * ASAuthorizationPlatformPublicKeyCredentialProvider support). WebAuthn isn't usable directly
 * inside a webview, so on iOS passkey creation goes through this native bridge instead.
 */
export const isPasskeyBridgeAvailable = async (): Promise<boolean> => {
  const bridge = getPasskeyBridge()
  if (typeof bridge?.isAvailable !== 'function') {
    return false
  }
  try {
    return await bridge.isAvailable()
  } catch {
    return false
  }
}

type RegistrationChallengeResponse = {
  challenge: string
  options: ServerRegistrationOptions
}

type AuthenticationChallengeResponse = {
  challenge_id: string
  challenge: string
  options: PublicKeyCredentialRequestOptions
}

type AuthenticationTokens = {
  access_token: string
  refresh_token: string
  token_type: string
  expires_in: number
  scope: string
  created_at: number
}

export type PasskeyAssertion = {
  challenge_id: string
  id: string
  rawId: string
  type: string
  response: {
    clientDataJSON: string
    authenticatorData: string
    signature: string
    userHandle?: string
  }
}

/**
 * Check if WebAuthn is supported by the browser
 */
export const isWebAuthnSupported = (): boolean => {
  return window?.PublicKeyCredential !== undefined && typeof window.PublicKeyCredential === 'function'
}

/**
 * Check if platform authenticator (Face ID, Touch ID, Windows Hello) is available
 */
export const isPlatformAuthenticatorAvailable = async (): Promise<boolean> => {
  if (!isWebAuthnSupported()) {
    return false
  }

  try {
    return await PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable()
  } catch (error) {
    logEvent('webauthn_platform_check_error', undefined, { error: String(error) })
    return false
  }
}

/**
 * Helper to convert base64url to Uint8Array
 */
const base64UrlToUint8Array = (base64url: string): BufferSource => {
  const padding = '='.repeat((4 - (base64url.length % 4)) % 4)
  const base64 = base64url.replace(/-/g, '+').replace(/_/g, '/') + padding
  return Uint8Array.from(atob(base64), (c) => c.charCodeAt(0))
}

/**
 * Helper to convert Uint8Array to base64url
 */
const uint8ArrayToBase64Url = (buffer: Uint8Array): string => {
  const base64 = window.btoa(String.fromCharCode.apply(null, Array.from(buffer)))
  return base64.replace(/\+/g, '-').replace(/\//g, '_').replace(/=/g, '')
}

export type CreatedWebAuthnCredential = {
  id: string
  device_nickname: string | null
  created_at: string
}

const getRegistrationChallenge = async (): Promise<RegistrationChallengeResponse> => {
  const challengeResponse = await directoryApiClient.post<RegistrationChallengeResponse>(
    '/api/auth/webauthn/registrations/challenges',
  )
  return challengeResponse.data
}

const verifyRegistration = async (
  attestation: RegistrationAttestation,
  deviceNickname?: string,
  passkeyAssertion?: PasskeyAssertion,
): Promise<CreatedWebAuthnCredential> => {
  const verifyResponse = await directoryApiClient.post<CreatedWebAuthnCredential>(
    '/api/auth/webauthn/registrations/verifications',
    {
      ...attestation,
      device_nickname: deviceNickname,
      ...(passkeyAssertion && { passkey_assertion: passkeyAssertion }),
    },
  )
  return verifyResponse.data
}

/**
 * Register a new passkey for the current user. Adding a passkey while enforce_passkey is
 * already on requires proof of an existing one first — pass `requiresStepUp: true` when the
 * caller knows enforcement is currently on.
 */
export const registerPasskey = async (
  deviceNickname?: string,
  requiresStepUp = false,
): Promise<CreatedWebAuthnCredential> => {
  if (!isWebAuthnSupported()) {
    throw new Error('WebAuthn is not supported in this browser')
  }

  const passkeyAssertion = requiresStepUp ? await getPasskeyStepUpAssertion() : undefined

  const { challenge, options } = await getRegistrationChallenge()

  const credentialCreationOptions: CredentialCreationOptions = {
    publicKey: {
      ...options,
      challenge: base64UrlToUint8Array(challenge),
      user: {
        ...options.user,
        id: base64UrlToUint8Array(options.user.id),
      },
      excludeCredentials: options.excludeCredentials?.map((cred) => ({
        ...cred,
        id: base64UrlToUint8Array(cred.id),
      })),
    },
  }

  logEvent('webauthn_registration_start')
  const credential = await navigator.credentials.create(credentialCreationOptions)

  if (!credential || credential.type !== 'public-key') {
    logEvent('webauthn_registration_failed', undefined, { reason: 'invalid_credential_type' })
    throw new Error('Failed to create credential')
  }

  const pkCredential = credential as PublicKeyCredential
  const response = pkCredential.response as AuthenticatorAttestationResponse

  const created = await verifyRegistration(
    {
      id: pkCredential.id,
      rawId: uint8ArrayToBase64Url(new Uint8Array(pkCredential.rawId)),
      type: pkCredential.type,
      response: {
        clientDataJSON: uint8ArrayToBase64Url(new Uint8Array(response.clientDataJSON)),
        attestationObject: uint8ArrayToBase64Url(new Uint8Array(response.attestationObject)),
      },
    },
    deviceNickname,
    passkeyAssertion,
  )
  logEvent('webauthn_registration_success')
  return created
}

/**
 * Register a new passkey via the native app's passkey bridge (iOS/Android webview, no browser WebAuthn).
 */
export const registerPasskeyViaBridge = async (
  platform: 'ios' | 'android',
  deviceNickname?: string,
): Promise<CreatedWebAuthnCredential> => {
  const bridge = getPasskeyBridge()
  if (typeof bridge?.register !== 'function') {
    throw new Error('Native passkey bridge is not available')
  }

  const { challenge, options } = await getRegistrationChallenge()
  const requestOptions = { ...options, challenge }

  logEvent('webauthn_registration_start', undefined, { via: 'native_bridge' })
  const rawAttestation = await bridge.register(platform === 'android' ? JSON.stringify(requestOptions) : requestOptions)
  const attestation: RegistrationAttestation =
    typeof rawAttestation === 'string' ? JSON.parse(rawAttestation) : rawAttestation
  const created = await verifyRegistration(attestation, deviceNickname)
  logEvent('webauthn_registration_success', undefined, { via: 'native_bridge' })
  return created
}

const getAuthenticationChallenge = async (): Promise<AuthenticationChallengeResponse> => {
  const challengeResponse = await directoryApiClient.post<AuthenticationChallengeResponse>(
    '/api/auth/webauthn/authentications/challenges',
  )
  return challengeResponse.data
}

// Authenticated and scoped to the current account, unlike the login challenge endpoint above —
// a step-up assertion must prove possession for THIS action, not just replay any assertion the
// account's passkey happened to produce (e.g. from a concurrent login).
const getStepUpChallenge = async (): Promise<AuthenticationChallengeResponse> => {
  const accessToken = getAccessToken()
  const challengeResponse = await directoryApiClient.post<AuthenticationChallengeResponse>(
    '/api/auth/webauthn/step_up/challenges',
    undefined,
    accessToken ? { headers: { Authorization: `Bearer ${accessToken}` } } : undefined,
  )
  return challengeResponse.data
}

const buildCredentialRequestOptions = (
  { challenge, options }: AuthenticationChallengeResponse,
  overrides?: { allowCredentials?: PublicKeyCredentialDescriptor[] } & Partial<
    Omit<CredentialRequestOptions, 'publicKey'>
  >,
): CredentialRequestOptions => {
  const { allowCredentials, ...rest } = overrides ?? {}
  return {
    publicKey: {
      ...options,
      challenge: base64UrlToUint8Array(challenge),
      allowCredentials:
        allowCredentials ??
        options.allowCredentials?.map((cred) => ({
          ...cred,
          id: base64UrlToUint8Array(cred.id as unknown as string),
        })),
    },
    ...rest,
  }
}

/**
 * Prompt the platform authenticator for an assertion and serialize it, without verifying it or
 * minting tokens. Used both for signing in and for step-up checks that gate a sensitive account
 * change (e.g. disabling enforce_passkey).
 */
const getPasskeyAssertion = async (
  credentialRequestOptions: CredentialRequestOptions,
  challengeId: string,
): Promise<PasskeyAssertion> => {
  const credential = await navigator.credentials.get(credentialRequestOptions)

  if (!credential || credential.type !== 'public-key') {
    throw new Error('Failed to authenticate')
  }

  const pkCredential = credential as PublicKeyCredential
  const response = pkCredential.response as AuthenticatorAssertionResponse

  return {
    challenge_id: challengeId,
    id: pkCredential.id,
    rawId: uint8ArrayToBase64Url(new Uint8Array(pkCredential.rawId)),
    type: pkCredential.type,
    response: {
      clientDataJSON: uint8ArrayToBase64Url(new Uint8Array(response.clientDataJSON)),
      authenticatorData: uint8ArrayToBase64Url(new Uint8Array(response.authenticatorData)),
      signature: uint8ArrayToBase64Url(new Uint8Array(response.signature)),
      userHandle: response.userHandle ? uint8ArrayToBase64Url(new Uint8Array(response.userHandle)) : undefined,
    },
  }
}

/**
 * Collect a passkey assertion to prove possession of a registered credential, for a step-up
 * check (e.g. disabling enforce_passkey or deleting the last passkey) rather than sign-in.
 */
export const getPasskeyStepUpAssertion = async (): Promise<PasskeyAssertion> => {
  if (!isWebAuthnSupported()) {
    throw new Error('WebAuthn is not supported in this browser')
  }

  const challengeResponse = await getStepUpChallenge()
  const credentialRequestOptions = buildCredentialRequestOptions(challengeResponse)
  return getPasskeyAssertion(credentialRequestOptions, challengeResponse.challenge_id)
}

/**
 * Authenticate with a passkey
 */
export const authenticateWithPasskey = async (): Promise<AuthenticationTokens> => {
  if (!isWebAuthnSupported()) {
    throw new Error('WebAuthn is not supported in this browser')
  }

  const challengeResponse = await getAuthenticationChallenge()
  const credentialRequestOptions = buildCredentialRequestOptions(challengeResponse)

  logEvent('webauthn_authentication_start')
  let assertion: PasskeyAssertion
  try {
    assertion = await getPasskeyAssertion(credentialRequestOptions, challengeResponse.challenge_id)
  } catch (error) {
    logEvent('webauthn_authentication_failed', undefined, { reason: 'invalid_credential_type' })
    throw error
  }

  let verifyResponse
  try {
    verifyResponse = await directoryApiClient.post<AuthenticationTokens>(
      '/api/auth/webauthn/authentications/verifications',
      assertion,
    )
  } catch (error) {
    if (axios.isAxiosError(error) && error.response?.data?.error === 'sso_forbidden') {
      throw new SsoForbiddenError()
    }
    throw error
  }

  const tokens = verifyResponse.data
  saveTokens(tokens.access_token, tokens.refresh_token)

  logEvent('webauthn_authentication_success')
  return tokens
}

/**
 * List all passkeys for the current user
 */
export const listPasskeys = async (): Promise<WebAuthnCredential[]> => {
  const accessToken = getAccessToken()
  const response = await directoryApiClient.get<{ credentials: WebAuthnCredential[] }>(
    '/api/auth/webauthn/credentials',
    accessToken ? { headers: { Authorization: `Bearer ${accessToken}` } } : undefined,
  )
  return response.data.credentials
}

/**
 * Rename a passkey
 */
export const renamePasskey = async (credentialId: string, newName: string): Promise<void> => {
  const accessToken = getAccessToken()
  await directoryApiClient.patch(
    `/api/auth/webauthn/credentials/${credentialId}`,
    { device_nickname: newName },
    accessToken ? { headers: { Authorization: `Bearer ${accessToken}` } } : undefined,
  )
}

/**
 * Delete a passkey. Deleting the last credential while enforce_passkey is on lifts enforcement,
 * so it requires a fresh passkey assertion — pass `requiresAssertion: true` when the caller
 * knows this is the last credential and enforcement is currently on.
 */
export const deletePasskey = async (
  credentialId: string,
  externalId: string,
  requiresAssertion = false,
): Promise<void> => {
  const accessToken = getAccessToken()
  const passkeyAssertion = requiresAssertion ? await getPasskeyStepUpAssertion() : undefined
  // Sent as a header rather than a DELETE body: some proxy/WAF layers strip bodies from DELETE
  // requests, which would make deleting the last credential fail with no way to recover. The
  // assertion is single-use and bound to its challenge, so carrying it in a header is no more
  // sensitive than carrying it in the body. Base64 keeps the JSON header-safe.
  await directoryApiClient.delete(`/api/auth/webauthn/credentials/${credentialId}`, {
    headers: {
      ...(accessToken && { Authorization: `Bearer ${accessToken}` }),
      ...(passkeyAssertion && { 'X-Passkey-Assertion': btoa(JSON.stringify(passkeyAssertion)) }),
    },
  })
  await signalUnknownCredential(externalId)
  logEvent('webauthn_credential_deleted')
}

export type LoginSettings = {
  enforce_passkey: boolean
  eligible: boolean
  /** False while the enforce_passkey_for_login switch is off, in which case the toggle is hidden. */
  enforce_passkey_for_login_enabled: boolean
}

/**
 * Get the passkey login settings for the current user
 */
export const getLoginSettings = async (): Promise<LoginSettings> => {
  const accessToken = getAccessToken()
  const response = await directoryApiClient.get<LoginSettings>(
    '/api/auth/webauthn/login_settings',
    accessToken ? { headers: { Authorization: `Bearer ${accessToken}` } } : undefined,
  )
  return response.data
}

/**
 * Enable or disable blocking password login for the current user. Disabling while currently
 * enforced requires a fresh passkey assertion, since a hijacked session alone shouldn't be able
 * to lift enforcement — pass the current `wasEnforced` state so a false->false no-op (which the
 * server doesn't guard) doesn't prompt for a passkey touch it doesn't need.
 */
export const updateLoginSettings = async (
  enforcePasskey: boolean,
  wasEnforced: boolean,
): Promise<{ enforce_passkey: boolean }> => {
  const accessToken = getAccessToken()
  const passkeyAssertion = !enforcePasskey && wasEnforced ? await getPasskeyStepUpAssertion() : undefined
  const response = await directoryApiClient.patch<{ enforce_passkey: boolean }>(
    '/api/auth/webauthn/login_settings',
    { enforce_passkey: enforcePasskey, ...(passkeyAssertion && { passkey_assertion: passkeyAssertion }) },
    accessToken ? { headers: { Authorization: `Bearer ${accessToken}` } } : undefined,
  )
  return response.data
}

/**
 * Public precheck: whether password login is blocked for this sign-in identifier (email or
 * national identifier / CPF), so the sign-in form can skip the password step and prompt for
 * a passkey instead.
 */
export const isPasswordLoginBlocked = async (identifier: string): Promise<boolean> => {
  const response = await directoryApiClient.get<{ enforce_passkey: boolean }>(
    '/api/auth/webauthn/login_settings/status',
    { params: { identifier } },
  )
  return response.data.enforce_passkey
}

export const isConditionalMediationAvailable = async (): Promise<boolean> => {
  if (!isWebAuthnSupported()) return false

  try {
    return (
      typeof PublicKeyCredential.isConditionalMediationAvailable === 'function' &&
      (await PublicKeyCredential.isConditionalMediationAvailable())
    )
  } catch {
    return false
  }
}

const requestAndVerifyPasskey = async (
  credentialRequestOptions: CredentialRequestOptions,
  challengeId: string,
): Promise<AuthenticationTokens> => {
  const assertion = await getPasskeyAssertion(credentialRequestOptions, challengeId)

  let verifyResponse
  try {
    verifyResponse = await directoryApiClient.post<AuthenticationTokens>(
      '/api/auth/webauthn/authentications/verifications',
      assertion,
    )
  } catch (error) {
    if (axios.isAxiosError(error) && error.response?.data?.error === 'sso_forbidden') {
      throw new SsoForbiddenError()
    }
    throw error
  }

  const tokens = verifyResponse.data
  saveTokens(tokens.access_token, tokens.refresh_token)
  return tokens
}

export const authenticateWithConditionalUI = async (
  abortController: AbortController,
): Promise<AuthenticationTokens> => {
  const challengeResponse = await getAuthenticationChallenge()
  const credentialRequestOptions = buildCredentialRequestOptions(challengeResponse, {
    allowCredentials: [],
    signal: abortController.signal,
    mediation: 'conditional' as CredentialMediationRequirement,
  })

  logEvent('webauthn_conditional_ui_start')
  const tokens = await requestAndVerifyPasskey(credentialRequestOptions, challengeResponse.challenge_id)
  logEvent('webauthn_conditional_ui_success')
  return tokens
}

export const webAuthnService = {
  isSupported: isWebAuthnSupported,
  isConditionalMediationAvailable,
  authenticateWithConditionalUI,
  registerPasskey,
  isPasskeyBridgeAvailable,
  registerPasskeyViaBridge,
  authenticateWithPasskey,
  listPasskeys,
  renamePasskey,
  deletePasskey,
  getLoginSettings,
  updateLoginSettings,
  isPasswordLoginBlocked,
}
